import { Base } from '../../abstract/base.model';
import { isRecord } from '../utils/is-record';
import { nullableString } from '../utils/wire-value-utils';
import { CertificationOwnerType } from './certification-owner-type.model';
import { CertificationStatus } from './certification-status.model';
import { CertificationType } from './certification-type.model';

/**
 * An organization or member certification, built from the hub's `/admin/certifications` JSON
 * (hub `data-models/certification.ts`). Writable fields the hub didn't send stay `undefined`
 * (an explicit `null` stays `null`). Dates stay as the hub sent them, so they round-trip unchanged.
 */
export class Certification extends Base {
  // Writable via PUT /admin/certifications/:id (the hub's CERTIFICATION_WRITABLE)
  certificateNumber?: string | null;
  issuedDate?: string | null;
  expirationDate?: string | null;
  issuingAuthority?: string | null;
  state?: string | null;
  /** The S3 key of the document; open it through `getDownloadUrl`. */
  documentUrl?: string | null;
  documentFileName?: string | null;
  notes?: string | null;
  status?: CertificationStatus | null;

  // Set on create only
  certificationType?: CertificationType | null;
  ownerType?: CertificationOwnerType | null;
  organizationId?: string | null;
  profileId?: string | null;
  previousCertificationId?: string | null;

  // Read-only audit fields
  createdBy?: string | null;
  updatedBy?: string | null;
  deletedBy?: string | null;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.certificateNumber = nullableString(source['certificateNumber']);
    this.issuedDate = nullableString(source['issuedDate']);
    this.expirationDate = nullableString(source['expirationDate']);
    this.issuingAuthority = nullableString(source['issuingAuthority']);
    this.state = nullableString(source['state']);
    this.documentUrl = nullableString(source['documentUrl']);
    this.documentFileName = nullableString(source['documentFileName']);
    this.notes = nullableString(source['notes']);
    this.status = nullableString(source['status']) as CertificationStatus | null | undefined;
    this.certificationType = nullableString(source['certificationType']) as CertificationType | null | undefined;
    this.ownerType = nullableString(source['ownerType']) as CertificationOwnerType | null | undefined;
    this.organizationId = nullableString(source['organizationId']);
    this.profileId = nullableString(source['profileId']);
    this.previousCertificationId = nullableString(source['previousCertificationId']);
    this.createdBy = nullableString(source['createdBy']);
    this.updatedBy = nullableString(source['updatedBy']);
    this.deletedBy = nullableString(source['deletedBy']);
  }
}
